import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  Box,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { Edit, Delete, Add, Remove } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Product } from '../types/entities';

type SortField = 'name' | 'amount' | 'sell_price' | 'createdAt';
type SortDirection = 'asc' | 'desc' | 'default';

interface ProductsTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onUpdateAmount?: (product: Product, newAmount: number) => void;
}

const ProductsTable: React.FC<ProductsTableProps> = ({ products, onEdit, onDelete, onUpdateAmount }) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => {
        if (prev === 'asc') return 'desc';
        if (prev === 'desc') return 'default';
        return 'asc';
      });
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedProducts = [...(products || [])].sort((a, b) => {
    if (sortDirection === 'default') return 0;

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'amount':
        aValue = a.amount;
        bValue = b.amount;
        break;
      case 'sell_price':
        aValue = a.sell_price ? parseFloat(a.sell_price) : 0;
        bValue = b.sell_price ? parseFloat(b.sell_price) : 0;
        break;
      case 'createdAt':
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatPrice = (price: string | null) => {
    if (!price) return t('products.noPrice');
    const numPrice = parseFloat(price);
    const formatted = numPrice.toFixed(2).replace('.', ',');
    // Remove ,00 for whole numbers
    const cleanPrice = formatted.endsWith(',00') ? formatted.slice(0, -3) : formatted;
    return `${cleanPrice} zł`;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedProducts.map((product) => (
          <Card key={product.id} sx={{ boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {product.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('products.amount')}:
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onUpdateAmount?.(product, Math.max(0, product.amount - 1))}
                        disabled={product.amount <= 0}
                        sx={{ 
                          padding: '4px',
                          '& .MuiSvgIcon-root': { fontSize: '1rem' }
                        }}
                      >
                        <Remove />
                      </IconButton>
                      <Typography variant="body2" sx={{ minWidth: '20px', textAlign: 'center' }}>
                        {product.amount}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onUpdateAmount?.(product, product.amount + 1)}
                        sx={{ 
                          padding: '4px',
                          '& .MuiSvgIcon-root': { fontSize: '1rem' }
                        }}
                      >
                        <Add />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('products.sellPrice')}: {formatPrice(product.sell_price)}
                    </Typography>
                    {product.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {product.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <IconButton
                    color="primary"
                    onClick={() => onEdit(product)}
                    size="medium"
                    sx={{ 
                      padding: '8px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem'
                      }
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => onDelete(product)}
                    size="medium"
                    sx={{ 
                      padding: '8px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem'
                      }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.5)',
          },
        },
      }}
    >
      <Table sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={sortField === 'name' && sortDirection !== 'default'}
                direction={sortField === 'name' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('name')}
                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
              >
                {t('common.name')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 100 }}>
              <TableSortLabel
                active={sortField === 'amount' && sortDirection !== 'default'}
                direction={sortField === 'amount' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('amount')}
                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
              >
                {t('products.amount')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={sortField === 'sell_price' && sortDirection !== 'default'}
                direction={sortField === 'sell_price' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('sell_price')}
                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
              >
                {t('products.sellPrice')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 200, fontWeight: 'bold', fontSize: '1rem' }}>
              {t('common.description')}
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedProducts.map((product) => (
            <TableRow key={product.id}>
              <TableCell sx={{ fontSize: '1rem' }}>
                {product.name}
              </TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => onUpdateAmount?.(product, Math.max(0, product.amount - 1))}
                    disabled={product.amount <= 0}
                    sx={{ 
                      padding: '4px',
                      '& .MuiSvgIcon-root': { fontSize: '1rem' }
                    }}
                  >
                    <Remove />
                  </IconButton>
                  <Typography variant="body2" sx={{ minWidth: '20px', textAlign: 'center' }}>
                    {product.amount}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onUpdateAmount?.(product, product.amount + 1)}
                    sx={{ 
                      padding: '4px',
                      '& .MuiSvgIcon-root': { fontSize: '1rem' }
                    }}
                  >
                    <Add />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>
                {formatPrice(product.sell_price)}
              </TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>
                {product.description || '-'}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <IconButton
                    color="primary"
                    onClick={() => onEdit(product)}
                    size="medium"
                    sx={{ 
                      padding: '8px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem'
                      }
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => onDelete(product)}
                    size="medium"
                    sx={{ 
                      padding: '8px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem'
                      }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductsTable;
